
import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Camera, Video, Heart, Eye, Upload, TrendingUp } from 'lucide-react';
import PermissionGate from '@/components/rbac/PermissionGate';

const CreativeDashboard: React.FC = () => {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <PermissionGate permission="upload_content">
          <Card className="bg-gray-800 border-gray-700">
            <CardContent className="p-4 flex items-center space-x-4">
              <Camera className="h-8 w-8 text-sports-green" />
              <div>
                <h4 className="font-semibold text-white">Photos Uploaded</h4>
                <p className="text-sm text-gray-300">1,247 this month</p>
              </div>
            </CardContent>
          </Card>
        </PermissionGate>

        <PermissionGate permission="upload_content">
          <Card className="bg-gray-800 border-gray-700">
            <CardContent className="p-4 flex items-center space-x-4">
              <Video className="h-8 w-8 text-sports-green" />
              <div>
                <h4 className="font-semibold text-white">Videos Created</h4>
                <p className="text-sm text-gray-300">89 this month</p>
              </div>
            </CardContent>
          </Card>
        </PermissionGate>

        <PermissionGate permission="view_analytics">
          <Card className="bg-gray-800 border-gray-700">
            <CardContent className="p-4 flex items-center space-x-4">
              <Eye className="h-8 w-8 text-sports-green" />
              <div>
                <h4 className="font-semibold text-white">Total Views</h4>
                <p className="text-sm text-gray-300">45,320 views</p>
              </div>
            </CardContent>
          </Card>
        </PermissionGate>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <PermissionGate permission="upload_content">
          <Card className="bg-gray-800 border-gray-700">
            <CardContent className="p-6">
              <h4 className="font-semibold mb-4 text-white flex items-center gap-2">
                <Upload className="h-5 w-5 text-sports-green" />
                Recent Uploads
              </h4>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-gray-700 rounded-md">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-sports-green/20 rounded-md flex items-center justify-center">
                      <Camera className="h-5 w-5 text-sports-green" />
                    </div>
                    <div>
                      <span className="text-white font-medium">Championship Final</span>
                      <p className="text-sm text-gray-300">Photo Gallery</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-400">
                    <Heart className="h-4 w-4" />
                    <span>234</span>
                  </div>
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-700 rounded-md">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-sports-green/20 rounded-md flex items-center justify-center">
                      <Video className="h-5 w-5 text-sports-green" />
                    </div>
                    <div>
                      <span className="text-white font-medium">Game Highlights</span>
                      <p className="text-sm text-gray-300">Video Montage</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-400">
                    <Eye className="h-4 w-4" />
                    <span>1.2k</span>
                  </div>
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-700 rounded-md">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-sports-green/20 rounded-md flex items-center justify-center">
                      <Camera className="h-5 w-5 text-sports-green" />
                    </div>
                    <div>
                      <span className="text-white font-medium">Team Portraits</span>
                      <p className="text-sm text-gray-300">Photo Session</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-400">
                    <Heart className="h-4 w-4" />
                    <span>156</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </PermissionGate>

        <PermissionGate permission="view_analytics">
          <Card className="bg-gray-800 border-gray-700">
            <CardContent className="p-6">
              <h4 className="font-semibold mb-4 text-white flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-sports-green" />
                Performance Analytics
              </h4>
              <div className="space-y-4">
                <div className="p-4 bg-gray-700 rounded-lg">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-white font-medium">Engagement Rate</span>
                    <span className="text-sports-green font-bold">12.3%</span>
                  </div>
                  <div className="w-full bg-gray-600 rounded-full h-2">
                    <div className="bg-sports-green h-2 rounded-full" style={{ width: '12.3%' }}></div>
                  </div>
                </div>
                <div className="p-4 bg-gray-700 rounded-lg">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-white font-medium">Content Reach</span>
                    <span className="text-sports-green font-bold">8.7k</span>
                  </div>
                  <p className="text-sm text-gray-300">+15% from last month</p>
                </div>
                <div className="p-4 bg-gray-700 rounded-lg">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-white font-medium">Follower Growth</span>
                    <span className="text-sports-green font-bold">+127</span>
                  </div>
                  <p className="text-sm text-gray-300">New followers this week</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </PermissionGate>
      </div>

      <PermissionGate permission="tag_content">
        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="p-6">
            <h4 className="font-semibold mb-4 text-white">Content Tagging & Organization</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-gray-700 p-4 rounded-lg">
                <h5 className="font-medium text-white mb-2">Tagged Teams</h5>
                <p className="text-sm text-gray-300 mb-3">Connect your content with teams and players</p>
                <div className="flex flex-wrap gap-2">
                  <span className="px-2 py-1 bg-sports-green/20 text-sports-green rounded text-xs">Eagles FC</span>
                  <span className="px-2 py-1 bg-sports-green/20 text-sports-green rounded text-xs">Thunder Hawks</span>
                  <span className="px-2 py-1 bg-sports-green/20 text-sports-green rounded text-xs">Storm United</span>
                </div>
              </div>
              <div className="bg-gray-700 p-4 rounded-lg">
                <h5 className="font-medium text-white mb-2">Popular Tags</h5>
                <p className="text-sm text-gray-300 mb-3">Most used tags in your content</p>
                <div className="flex flex-wrap gap-2">
                  <span className="px-2 py-1 bg-gray-600 text-gray-300 rounded text-xs">#championship</span>
                  <span className="px-2 py-1 bg-gray-600 text-gray-300 rounded text-xs">#highlights</span>
                  <span className="px-2 py-1 bg-gray-600 text-gray-300 rounded text-xs">#teamwork</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </PermissionGate>
    </div>
  );
};

export default CreativeDashboard;
